import { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";
import type { HtagSummary } from "./htag-types";

interface HtagDataResult {
  data: HtagSummary | null;
  loading: boolean;
  error: string | null;
}

// Session-level cache — survives component unmounts, cleared on page refresh
const cache = new Map<string, HtagSummary>();

/** Read-only access to the session cache. Does NOT trigger fetches. */
export function getCachedHtagData(lgaId: string, htagType: string): HtagSummary | null {
  return cache.get(`${lgaId}::${htagType}`) ?? null;
}

/**
 * Lazily fetch HTAG market data for a (lgaId, propertyType) pair.
 * Only fires when `enabled` becomes true. Caches per session so
 * collapsing/re-expanding doesn't re-fetch.
 */
export function useHtagData(
  lgaId: string | undefined,
  htagType: string,
  addressKey: string | undefined,
  enabled: boolean,
): HtagDataResult {
  const [data, setData] = useState<HtagSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !lgaId || fetchedRef.current) return;

    const cacheKey = `${lgaId}::${htagType}`;
    const cached = cache.get(cacheKey);
    if (cached) {
      setData(cached);
      setLoading(false);
      setError(null);
      fetchedRef.current = true;
      return;
    }

    setLoading(true);
    setError(null);
    fetchedRef.current = true;

    supabase!.functions.invoke("htag-data", {
      body: {
        lga_id: lgaId,
        property_type: htagType,
        ...(addressKey ? { address_key: addressKey } : {}),
      },
    })
      .then((res) => {
        if (res.error) throw new Error(res.error.message);
        const result = res.data as HtagSummary;
        cache.set(cacheKey, result);
        setData(result);
      })
      .catch((e) => setError(String((e as Error)?.message ?? "Failed to load market data")))
      .finally(() => setLoading(false));
  }, [enabled, lgaId, htagType, addressKey]);

  // Reset when property changes (different lgaId/type)
  useEffect(() => {
    fetchedRef.current = false;
    const cacheKey = `${lgaId}::${htagType}`;
    const cached = cache.get(cacheKey);
    if (cached) {
      setData(cached);
      setLoading(false);
      setError(null);
    } else {
      setData(null);
      setLoading(false);
      setError(null);
    }
  }, [lgaId, htagType]);

  return { data, loading, error };
}
